/**
 * 站方教材曲库（首页「曲谱」区里「教材」那个来源）。
 *
 * ## 正文现在在数据库里
 *
 * 上一版是 `public/examples/catalog.json` + `library/*.spnmn` 一套静态文件。
 * 管理端要能隐藏/删除/新增之后，静态文件不再是可接受的落点（nginx 只读、
 * 写 media/ 会让备份覆盖不到），所以搬进了 `library_items` 表，接口是
 * `/api/library` 与 `/api/library/<id>`。
 *
 * ## 为什么还留着静态兜底
 *
 * `public/examples/` 下的产物**没有删**：接口挂了（后端重启、网络抖动、
 * 部署途中）时，首页还能用静态目录把曲库显示出来 —— 访客不该因为一次 500
 * 就看不到任何曲子。代价是 dist 里多 2MB 静态文件，只在真正点开时才下载。
 *
 * 判断用哪边只看 `item.file`：数据库来的条目 `file` 是空串，
 * 静态目录里的条目带着 `library/etude-0001.spnmn`。一个字段定去路，不用额外传参。
 */

import { api } from './saas/api'

export type LibraryCategory = {
	id: string
	name: string
	desc: string
	/** 该分类里最基础的难度档，用来在空分类上提示 */
	minLevel: string
	count: number
}

export type LibraryItem = {
	id: string
	/**
	 * 静态文件相对路径（`library/etude-0001.spnmn`）。
	 * 数据库来的条目是空串 —— 见文件头注释。
	 */
	file: string
	title: string
	composer: string
	/** 入门 / 初级 / 中级 */
	level: string
	category: string
	/** N: 行数（声部行数，1~2）。曲库里刻意限成最多 2 行，见导入器的 LAYER_CAP。 */
	voices: number
	/** 导入时产生的近似告警条数，>0 说明有细节被简化 */
	warnings: number
	/** 被省略的音数（和弦内音 + 次要声部） */
	omitted: number
	/** 元数据可疑（源文件名乱码 / 字段污染），细节由脚本注释说明 */
	suspect: boolean
	/** 源文件相对路径，供人工校对 */
	src: string
	/** 管理端可见：是否被隐藏（公开接口不会返回隐藏项） */
	is_hidden?: boolean
	/**
	 * 编谱知识点（`多声部` / `鼓点行` / `转调` …）。只有 `category == 'demo'`
	 * （编谱示例）的条目才有；静态兜底目录里没有这个字段，所以是可选的。
	 */
	topics?: string[]
	/** 创建者/编谱人。空 = 公共领域作品，卡片按分类给默认文案。 */
	credit?: string
}

export type LibraryCatalog = {
	/** `db` = 正文在数据库；`static` = 在 public/examples/ 下 */
	storage?: 'db' | 'static'
	source: string
	license: string
	generatedAt: string
	categories: LibraryCategory[]
	items: LibraryItem[]
}

let cached: Promise<LibraryCatalog> | null = null

/**
 * 取曲库目录：**先问后端，失败回落静态目录**。
 *
 * 缓存 promise 而不是结果 —— 首页可能因状态更新多次调用，缓存结果会有
 * 「并发两次请求」的窗口；缓存 promise 天然去重。失败时清掉缓存，
 * 否则一次网络抖动会让整个会话都读不到曲库。
 */
export function fetchLibraryCatalog(): Promise<LibraryCatalog> {
	if (!cached) {
		cached = (async () => {
			try {
				const data = await api.get<LibraryCatalog>('/library')
				if (data?.items?.length) return {...data, storage: 'db' as const}
			} catch (err) {
				console.warn('曲库接口读不到，回落静态目录：', err)
			}
			const res = await fetch('./examples/catalog.json')
			if (!res.ok) throw new Error(`曲库目录读取失败（${res.status}）`)
			const data = await res.json() as LibraryCatalog
			// 老版本产物的 catalog.json 没有 storage 字段，缺省按静态处理
			return {...data, storage: data.storage ?? 'static' as const}
		})().catch(err => {
			cached = null
			throw err
		})
	}
	return cached
}

/**
 * 取单首正文。
 *
 * `item.file` 非空就是静态产物（兜底路径），否则走后端 —— 见文件头注释。
 */
export async function fetchLibraryScore(item: LibraryItem): Promise<string> {
	if (item.file) {
		const res = await fetch(`./examples/${item.file.split('/').map(encodeURIComponent).join('/')}`)
		if (!res.ok) throw new Error(`曲谱读取失败（${res.status}）`)
		return await res.text()
	}
	const data = await api.get<{content: string}>(`/library/${encodeURIComponent(item.id)}`)
	if (!data?.content) throw new Error('曲谱正文是空的')
	return data.content
}

/** 难度档，按学琴进度排 */
export const LIBRARY_LEVELS = ['入门', '初级', '中级'] as const

export type LibraryFilter = {
	category: string
	level: string
}

export function filterLibrary(items: LibraryItem[], filter: LibraryFilter): LibraryItem[] {
	return items.filter(item =>
		(filter.category == '' || item.category == filter.category)
		&& (filter.level == '' || item.level == filter.level))
}

/**
 * 首页卡片上的一行小字。
 *
 * 不显示 `omitted` 的具体数字 —— 那是转换细节，用户只关心「这首能不能弹」。
 * 但**含近似告警的要标出来**，否则弹到一半发现某小节不对，会以为是网站坏了。
 */
/**
 * 卡片上显示的曲名。
 *
 * 源数据的标题常把作曲家名又写了一遍（`Bertini Etude Op.29 No.1 in C major`、
 * `Czerny_ 160 Kurze Übungen op. 821 no. 10`），而卡片下面那行已经写了作曲家 ——
 * 重复的前缀会把真正区分作品的编号挤出可见区。去掉前缀后
 * `160 Kurze Übungen op. 821 no. 10` 才放得下「no. 10」。
 */
export function displayTitle(item: LibraryItem): string {
	const t = item.title
	// 用姓氏比对：作曲家字段可能是 `J.S. Bach`，标题里写的是 `Bach`
	const surname = item.composer.split(/[\s.]+/).filter(Boolean).pop()
	if (!surname || surname.length < 3 || surname == '佚名') return t
	const re = new RegExp(`^${surname.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*[_\\-–—:]*\\s*`, 'i')
	const trimmed = t.replace(re, '').trim()
	// 别把标题整条吃掉（有些曲名就是作曲家的名字，比如 `Anton Arensky`）
	return trimmed.length >= 4 ? trimmed : t
}

/** 卡片上的一行小字。 */
export function libraryBlurb(item: LibraryItem): string {
	const bits = [item.composer || '佚名']
	bits.push(item.voices >= 2 ? `${item.voices} 行` : '单旋律')
	// 只在缺得多到影响听感时才提。和弦已经按原样保留（`[135]`），现在 `omitted`
	// 只剩「幽灵声部」（源谱里只剩一两个音的转谱残留），正常曲子都是 0 ——
	// 这个标签因此变成了真·异常提示，而不是几乎每首都能挂上的通用免责声明。
	if (item.omitted > 500) bits.push('简化较多')
	return bits.join(' · ')
}

/**
 * 曲谱里有几行 N:（一个谱行块内的最大值）。
 *
 * 管理端「新增曲目」时给 `voices` 用 —— 卡片上要显示「2 行」。
 * ⚠️ 不能数全曲的 N 行总数：那是「声部数 × 谱行数」，一首 31 个谱行的二声部
 * 曲子会算出 62（导入脚本里踩过同一个坑）。
 */
export function countVoicesInContent(content: string): number {
	let max = 0
	let cur = 0
	for(const line of content.split('\n')) {
		const t = line.trim()
		if(t == '---' || t == '===') { max = Math.max(max, cur); cur = 0; continue }
		if(/^N/.test(line)) cur += 1
	}
	return Math.max(max, cur) || 1
}
